

import { apiSSO } from '@rfacil/shared/services';
import axios from "axios";
import { CrearUsuarioDetalleDTO } from './usuarioDetalle.Service';

export interface ContraseniasDTO
{
    Correo: string;
    PSSActual: string;
    NuevaContrasenia: string;
    ConfirmarContrasenia: string;
}

/**
 * @interface UsuarioDTO
 * @description Representa un usuario completo en el sistema con toda su información.
 * @property {number} Id - Identificador único del usuario.
 * @property {string} Usuario - Nombre de usuario (username).
 * @property {string} Correo - Correo electrónico del usuario.
 * @property {string} ContraseniaEncriptada - Contraseña del usuario encriptada.
 * @property {boolean} Estatus - Estado activo/inactivo del usuario.
 * @property {string} FechaAlta - Fecha de alta del usuario en formato ISO string.
 * @property {string} FechaBaja - Fecha de baja del usuario en formato ISO string.
 * @property {boolean} EsAdmin - Indica si el usuario es administrador.
 * @property {boolean} BorradoLogico - Indica si el usuario ha sido eliminado lógicamente (soft delete).
 */
export interface UsuarioDTO {
	Id: number;
  Usuario: string;
  Correo: string;
  ContraseniaEncriptada: string;
	Estatus: boolean;
	FechaAlta: string;
  FechaBaja: string;
	Rol: string | null;
  BorradoLogico: boolean;
  NombreCompleto: string;
  IdEmpresa: number;
  IdCorporativo: number | null;
  NombreEmpresa?: string | null;
  NombreCorporativo?: string | null;
  MensajeRespuesta?: string | null;
}

/**
 * @interface CrearUsuarioDTO
 * @description DTO utilizado para crear un nuevo usuario en el sistema.
 * @property {string} Usuario - Nombre de usuario (username) (requerido).
 * @property {string} Correo - Correo electrónico del usuario (requerido).
 * @property {string} ContraseniaEncriptada - Contraseña del usuario encriptada (requerido).
 * @property {boolean} Estatus - Estado inicial del usuario (activo/inactivo).
 * @property {string} FechaAlta - Fecha de alta del usuario en formato ISO string.
 * @property {boolean} EsAdmin - Indica si el usuario será administrador.
 * @property {string} Password - Contraseña en texto plano (se encriptará antes de guardar).
 */
export interface CrearUsuarioDTO
{
  Usuario: string;
  Correo: string;
  ContraseniaEncriptada: string;
  Estatus: boolean;
  FechaAlta: string;
  Rol?: string | null;
  Password: string;
}

/**
 * @interface EditarUsuarioDTO
 * @description DTO utilizado para editar un usuario existente en el sistema.
 * @property {number} Id - Identificador único del usuario a editar (requerido).
 * @property {string} Correo - Nuevo correo electrónico del usuario.
 * @property {boolean} Estatus - Nuevo estado del usuario (activo/inactivo).
 * @property {boolean} [EsAdmin] - Indica si el usuario es administrador (opcional).
 */
export interface EditarUsuarioDTO
{
  Id: number;
  Correo: string;
  Estatus: boolean;

}

/**
 * @interface EliminarUsuarioDTO
 * @description DTO utilizado para realizar un borrado lógico (soft delete) de un usuario.
 * @property {string} Usuario - Nombre de usuario a eliminar.
 * @property {string} Correo - Correo electrónico del usuario.
 * @property {string} ContraseniaEncriptada - Contraseña del usuario encriptada.
 * @property {boolean} Estatus - Estado del usuario (generalmente false para eliminación).
 * @property {boolean} BorradoLogico - Indica que se realizará un borrado lógico (true).
 * @property {string} FechaBaja - Fecha en que se realiza el borrado en formato ISO string.
 */
export interface EliminarUsuarioDTO
{
    Id: number;
    Estatus: boolean;
    BorradoLogico: boolean;
    FechaBaja: string;
}


/**
 * @interface UsuarioCompletoDTO
 * @description DTO utilizado para crear un usuario completo con su información principal y detalle.
 * @property {CrearUsuarioDTO} Usuario - Datos principales del usuario a crear.
 * @property {CrearUsuarioDetalleDTO} UsuarioDetalle - Datos detallados del usuario a crear (información personal, RFC, etc.).
 * @property {number | null} IdRol - Identificador del rol a asignar al usuario (puede ser null si no se asigna rol).
 * @property {number} IdEmpresa - Identificador de la empresa a la que pertenecerá el usuario (requerido).
 */
export interface UsuarioCompletoDTO {
  Usuario: CrearUsuarioDTO;
  UsuarioDetalle: CrearUsuarioDetalleDTO;
  IdRol: number | null;
  IdEmpresa: number;
}
/**
 * @interface UserCorporativoDTO
 * @description Representa un usuario corporativo completo en el sistema con toda su información.
 * @property {number} Id - Identificador único del usuario corporativo.
 * @property {string} Usuario - Nombre de usuario (username).
 * @property {string} Correo - Correo electrónico del usuario.
 * @property {string} ContraseniaEncriptada - Contraseña del usuario encriptada.
 * @property {boolean} Estatus - Estado activo/inactivo del usuario.
 * @property {string} FechaAlta - Fecha de alta del usuario en formato ISO string.
 * @property {string} FechaBaja - Fecha de baja del usuario en formato ISO string.
 * @property {string | null} Rol - Nombre del rol asignado al usuario o null si no tiene rol.
 * @property {boolean} BorradoLogico - Indica si el usuario ha sido eliminado lógicamente (soft delete).
 * @property {string} NombreCompleto - Nombre completo del usuario.
 * @property {number} IdCorporativo - Identificador del corporativo al que pertenece el usuario.
 */
export interface UserCorporativoDTO {
    Id: number;
    Usuario: string;
    Correo: string;
    ContraseniaEncriptada: string;
    Estatus: boolean;
    FechaAlta: string;
    FechaBaja: string;
    Rol: string | null;
    BorradoLogico: boolean;
    NombreCompleto: string;
    IdCorporativo: number;
    NombreCorporativo?: string | null;
}

/**
 * @interface UsuarioCorporativoCompletoDTO
 * @description DTO utilizado para crear un usuario corporativo completo con su información principal y detalle.
 * @property {CrearUsuarioDTO} Usuario - Datos principales del usuario corporativo a crear.
 * @property {CrearUsuarioDetalleDTO} UsuarioDetalle - Datos detallados del usuario a crear (información personal, RFC, etc.).
 * @property {number} IdCorporativo - Identificador del corporativo al que pertenecerá el usuario (requerido).
 */
export interface UsuarioCorporativoCompletoDTO {
    Usuario: CrearUsuarioDTO;
    UsuarioDetalle: CrearUsuarioDetalleDTO;
    IdCorporativo: number;
}


/**
 * @function createUser
 * @description Crea un nuevo usuario en el sistema junto con su información detallada.
 * @param {UsuarioCompletoDTO} data - Datos completos del usuario a crear (incluye Usuario y UsuarioDetalle).
 * @returns {Promise<UsuarioDTO>} Una promesa que se resuelve con el usuario creado incluyendo su Id asignado.
 * @throws {Error} Si ocurre un error al crear el usuario en la API (por ejemplo, usuario duplicado, correo duplicado, datos inválidos, etc.).
 */
export async function createUser(data: UsuarioCompletoDTO): Promise<UsuarioDTO> {
	try {
		const response = await apiSSO.post('/Usuario/CrearUsuario', data);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			console.error(`Error de API en createUser: ${error.response?.status} - ${error.response?.data}`, error);
			throw new Error(error.response?.data);
		} else {
			console.error('Error al crear el usuario:', error);
			throw new Error('Error inesperado al crear el usuario.');
		}
	}
}


/**
 * @function editUser
 * @description Edita un usuario existente en el sistema. Permite modificar el correo electrónico y el estatus del usuario.
 * @param {EditarUsuarioDTO} data - Datos del usuario a editar (Id, Correo, Estatus).
 * @returns {Promise<UsuarioDTO>} Una promesa que se resuelve con el usuario actualizado.
 * @throws {Error} Si ocurre un error al editar el usuario en la API (por ejemplo, usuario no encontrado, datos inválidos, etc.).
 */
export async function editUser(data: EditarUsuarioDTO): Promise<UsuarioDTO> {
  try {
    const response = await apiSSO.put('/Usuario/EditarUsuario', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en editUser: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error al editar el usuario:', error);
      throw new Error('Error inesperado al editar el usuario.');
    }
  }
}

/**
 * @function deleteUser
 * @description Realiza un borrado lógico (soft delete) de un usuario en el sistema.
 * El usuario no se elimina físicamente, sino que se marca como eliminado con BorradoLogico = true.
 * @param {EliminarUsuarioDTO} data - Datos del usuario a eliminar (Usuario, Correo, ContraseniaEncriptada, Estatus, BorradoLogico, FechaBaja).
 * @returns {Promise<UsuarioDTO>} Una promesa que se resuelve con el usuario eliminado (con BorradoLogico = true).
 * @throws {Error} Si ocurre un error al eliminar el usuario en la API (por ejemplo, usuario no encontrado, etc.).
 */
export async function deleteUser(data: EliminarUsuarioDTO): Promise<UsuarioDTO> {
  try {
    const response = await apiSSO.put('/Usuario/EliminarUsuario', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en deleteUser: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    }
    else {
      console.error('Error al eliminar el usuario:', error);
      throw new Error('Error inesperado al eliminar el usuario.');
    }
  }
}


/**
 * @function getUserById
 * @description Obtiene un usuario por su ID, incluyendo información de IdCorporativo e IdEmpresa.
 * @param {number} id - ID del usuario a obtener.
 * @returns {Promise<UsuarioDTO>} Una promesa que se resuelve con el usuario, incluyendo IdCorporativo e IdEmpresa si están disponibles.
 * @throws {Error} Si ocurre un error al obtener el usuario.
 */
export async function getUserById(Id: number): Promise<UsuarioDTO> {
	try {
		const response = await apiSSO.get(`/Usuario/ObtenerUsuarioxID/${Id}`);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			console.error(`Error de API en getUserById: ${error.response?.status} - ${error.response?.data}`, error);
			throw new Error(error.response?.data);
		} else {
			console.error('Error al obtener el usuario:', error);
			throw new Error('Error inesperado al obtener el usuario.');
		}
	}
}

/**
 * @function getUsersCorporative
 * @description Obtiene todos los usuarios corporativos registrados en el sistema.
 * @returns {Promise<UserCorporativoDTO[]>} Una promesa que se resuelve con un array de usuarios corporativos.
 * @throws {Error} Si ocurre un error al obtener los usuarios corporativos desde la API.
 */
export async function getUsersCorporative(): Promise<UserCorporativoDTO[]> {
	try {
		const response = await apiSSO.get(`/Usuario/ObtenerUsuariosCorporativos`);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			console.error(`Error de API en getUsersCorporative: ${error.response?.status} - ${error.response?.data}`, error);
			throw new Error(error.response?.data);
		} else {
			console.error('Error al obtener los Usuarios Corporativos:', error);
			throw new Error('Error inesperado al obtener los Usuarios Corporativos.');
		}
	}
}


/**
 * @function getUserCorporateById
 * @description Obtiene usuarios corporativos asociados a un corporativo específico por su ID.
 * @param {number} Id - Identificador del corporativo del cual se quieren obtener los usuarios.
 * @returns {Promise<UserCorporativoDTO[]>} Una promesa que se resuelve con un array de usuarios corporativos.
 * @throws {Error} Si ocurre un error al obtener los usuarios corporativos desde la API (por ejemplo, corporativo no encontrado, etc.).
 */
export async function getUserCorporateById(Id: number): Promise<UserCorporativoDTO[]> {
	try {
		const response = await apiSSO.get(`/Usuario/ObtenerUsuarioCorporativoxID/${Id}`);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			console.error(`Error de API en getUserCorporateById: ${error.response?.status} - ${error.response?.data}`, error);
			throw new Error(error.response?.data);
		} else {
			console.error('Error al obtener el Usuario Corporativo:', error);
			throw new Error('Error inesperado al obtener el Usuario Corporativo.');
		}
	}
}


/**
 * @function createUserCorporate
 * @description Crea un nuevo usuario corporativo en el sistema junto con su información detallada.
 * @param {UsuarioCorporativoCompletoDTO} data - Datos completos del usuario corporativo a crear (incluye Usuario, UsuarioDetalle e IdCorporativo).
 * @returns {Promise<UsuarioDTO>} Una promesa que se resuelve con el usuario corporativo creado incluyendo su Id asignado.
 * @throws {Error} Si ocurre un error al crear el usuario corporativo en la API (por ejemplo, usuario duplicado, correo duplicado, corporativo no encontrado, datos inválidos, etc.).
 */
export async function createUserCorporate(data: UsuarioCorporativoCompletoDTO): Promise<UsuarioDTO> {
  try {
    const response = await apiSSO.post('/Usuario/CrearUsuarioCorporativo', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en createUserCorporate: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error inesperado al crear el Usuario Corporativo:', error);
      throw new Error('Error inesperado al crear el Usuario Corporativo.');
    }
  }
}

/**
 * @function getUserCompanyById
 * @description Obtiene todos los usuarios de empresa asociados a un corporativo específico.
 * @param {number} IdCorporativo - Identificador del corporativo del cual se quieren obtener los usuarios de empresa.
 * @returns {Promise<UsuarioDTO[]>} Una promesa que se resuelve con un array de usuarios de empresa.
 * @throws {Error} Si ocurre un error al obtener los usuarios de empresa desde la API (por ejemplo, corporativo no encontrado, etc.).
 */
export async function getUserCompanyById(IdCorporativo: number): Promise<UsuarioDTO[]> {
	try {
		const response = await apiSSO.get(`/Usuario/ObtenerUsuariosEmpresa/${IdCorporativo}`);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			console.error(`Error de API en getUserCompanyById: ${error.response?.status} - ${error.response?.data}`, error);
			throw new Error(error.response?.data);
		} else {
			console.error('Error al obtener los Usuarios de la Empresa:', error);
			throw new Error('Error inesperado al obtener los Usuarios de la Empresa.');
		}
	}
}

/**
 * @function createUserCompany
 * @description Crea un nuevo usuario de empresa en el sistema junto con su información detallada.
 * @param {UsuarioCompletoDTO} data - Datos completos del usuario de empresa a crear (incluye Usuario, UsuarioDetalle, IdRol e IdEmpresa).
 * @returns {Promise<UsuarioDTO>} Una promesa que se resuelve con el usuario de empresa creado incluyendo su Id asignado.
 * @throws {Error} Si ocurre un error al crear el usuario de empresa en la API (por ejemplo, usuario duplicado, correo duplicado, empresa no encontrada, datos inválidos, etc.).
 */
export async function createUserCompany(data: UsuarioCompletoDTO): Promise<UsuarioDTO> {
  try {
    const response = await apiSSO.post('/Usuario/CrearUsuarioEmpresa', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en createUserCompany: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error inesperado al crear el Usuario de la Empresa:', error);
      throw new Error('Error inesperado al crear el Usuario de la Empresa.');
    }
  }
}

/**
 * @function getUserOperativeById
 * @description Obtiene todos los usuarios operativos asociados a una empresa específica.
 * @param {number} IdEmpresa - Identificador de la empresa de la cual se quieren obtener los usuarios operativos.
 * @returns {Promise<UsuarioDTO[]>} Una promesa que se resuelve con un array de usuarios operativos.
 * @throws {Error} Si ocurre un error al obtener los usuarios operativos desde la API (por ejemplo, empresa no encontrada, etc.).
 */
export async function getUserOperativeById(IdEmpresa: number): Promise<UsuarioDTO[]> {
	try {
		const response = await apiSSO.get(`/Usuario/ObtenerUsuariosOperativos/${IdEmpresa}`);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			console.error(`Error de API en getUserOperativeById: ${error.response?.status} - ${error.response?.data}`, error);
			throw new Error(error.response?.data);
		} else {
			console.error('Error al obtener los Usuarios Operativos:', error);
			throw new Error('Error inesperado al obtener los Usuarios Operativos.');
		}
	}
}


/**
 * @function getUserOperativeInvoice  (Usuarios para facturas)
 * @description Obtiene todos los usuarios operativos asociados a una empresa específica para facturas.
 * @param {number} IdEmpresa - Identificador de la empresa de la cual se quieren obtener los usuarios operativos de facturas.
 * @returns {Promise<UsuarioDTO[]>} Una promesa que se resuelve con un array de usuarios operativos para facturas.
 * @throws {Error} Si ocurre un error al obtener los usuarios operativos para facturas desde la API (por ejemplo, empresa no encontrada, etc.).
 */
export async function getUserOperativeInvoice(IdEmpresa: number): Promise<UsuarioDTO[]> {
	try {
		const response = await apiSSO.get(`/Usuario/ObtenerUsuariosOperativosFacturas/${IdEmpresa}`);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			console.error(`Error de API en getUserOperativeInvoice: ${error.response?.status} - ${error.response?.data}`, error);
			throw new Error(error.response?.data);
		} else {
			console.error('Error al obtener los Usuarios Operativos de Facturas:', error);
			throw new Error('Error inesperado al obtener los Usuarios Operativos de Facturas.');
		}
	}
}
/**
 * @function createUserOperative
 * @description Crea un nuevo usuario operativo en el sistema junto con su información detallada.
 * @param {UsuarioCompletoDTO} data - Datos completos del usuario operativo a crear (incluye Usuario, UsuarioDetalle, IdRol e IdEmpresa).
 * @returns {Promise<UsuarioDTO>} Una promesa que se resuelve con el usuario operativo creado incluyendo su Id asignado.
 * @throws {Error} Si ocurre un error al crear el usuario operativo en la API (por ejemplo, usuario duplicado, correo duplicado, empresa no encontrada, datos inválidos, etc.).
 */
export async function createUserOperative(data: UsuarioCompletoDTO): Promise<UsuarioDTO> {
  try {
    const response = await apiSSO.post('/Usuario/CrearUsuarioOperativo', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en createUserOperative: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error inesperado al crear el Usuario Operativo:', error);
      throw new Error('Error inesperado al crear el Usuario Operativo.');
    }
  }
}



/**
 * Solicita el envío de un correo para recuperar la contraseña.
 * Ajusta la ruta si tu API usa otro endpoint (por ejemplo bajo Login).
 */
export async function requestPasswordReset(correo: string): Promise<void> {
  try {
    await apiSSO.post('/Usuario/SolicitarRecuperacionContrasena', { Correo: correo.trim() });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en requestPasswordReset: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error inesperado al solicitar recuperación de contraseña:', error);
      throw new Error('Error inesperado al solicitar la recuperación de contraseña.');
    }
  }
}

export async function ChangePassword(data: ContraseniasDTO): Promise<void> {
  try {
    const response = await apiSSO.put('/Usuario/CambiarPSS', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en ChangePassword: ${error.response?.status} - ${error.response?.data}`, error);
      throw new Error(error.response?.data);
    } else {
      console.error('Error inesperado al cambiar la contraseña:', error);
      throw new Error('Error inesperado al cambiar la contraseña.');
    }
  }
}
